import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PlayIcon from '@mui/icons-material/PlayCircleOutline'
import { JOURS_FR, type SectionSite } from '@iobiz/shared'
import { ApiError } from '../api/client.js'
import { useSessionStore } from '../stores/sessionStore.js'
import {
  apercuVitrine,
  lireVitrine,
  type ArticlePublic,
  type SitePublic,
} from '../features/site/api.js'
import { ModaleRdvEnLigne } from '../features/site/ModaleRdvEnLigne.js'

/**
 * Le site vitrine d'une boutique — gabarit « vitrine-1 ».
 *
 * ⭐ En PUBLIC : aucune session, aucune réplique, aucun menu d'application.
 * C'est la page qu'un client lit avant d'être client, et elle doit s'ouvrir
 * aussi vite sur un téléphone en 3G que le reste de l'application sur un poste.
 *
 * ⭐ En APERÇU (`apercu`) : la même page, brouillon compris, réservée aux
 * connectés. Sans elle, l'administrateur ne découvrirait l'effet de ses
 * modifications qu'une fois le site public — il verrait son erreur en même
 * temps que ses clients.
 *
 * ⚠️ Tout vient du contenu enregistré par le gérant : les titres, l'ordre des
 * sections, les couleurs. Rien n'est écrit en dur ici sauf la manière de
 * dessiner — sans quoi chaque enseigne devrait passer par nous pour déplacer
 * un bloc.
 */
export function VitrinePage({ apercu = false }: { apercu?: boolean }): JSX.Element {
  /**
   * ⚠️ DEUX chemins vers la même page d'aperçu, et il en faut deux : le gérant
   * arrive par le slug de SA boutique, l'administrateur de plateforme par
   * l'identifiant d'une enseigne dont il n'est pas membre — sa session ne la
   * contient donc pas, et un aperçu qui ne saurait la résoudre lui répondrait
   * « boutique introuvable » sur une boutique qu'il administre.
   */
  const { slug, storeId: storeIdUrl } = useParams<{ slug?: string; storeId?: string }>()
  const boutiqueSession = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))
  const storeId = storeIdUrl ?? boutiqueSession?.storeId
  const [site, setSite] = useState<SitePublic | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  /**
   * La prestation pour laquelle on prend rendez-vous.
   *
   * ⭐ Le formulaire n'apparaît QU'APRÈS le choix d'un service. Le présenter
   * d'emblée obligeait le visiteur à redésigner dans une liste ce qu'il venait
   * de lire juste au-dessus — et une page qui s'ouvre sur un formulaire
   * ressemble à une démarche, pas à une vitrine.
   */
  const [serviceChoisi, setServiceChoisi] = useState<string | null>(null)
  /**
   * ⭐ La prise de rendez-vous s'ouvre en MODALE, comme l'agenda de la
   * boutique. Elle interrompt la lecture le temps de choisir une heure, puis
   * la rend — là où une section poussait le visiteur à faire défiler la page
   * jusqu'en bas et à y perdre ce qu'il regardait.
   */
  const [rdvOuvert, setRdvOuvert] = useState(false)
  /**
   * ⚠️ Le menu du site était simplement MASQUÉ sous 900 px, sans rien pour le
   * remplacer : sur téléphone — c'est-à-dire là où ces pages se lisent le plus
   * — « Horaires » et « Contact » devenaient introuvables, et le visiteur
   * devait faire défiler la page entière en espérant tomber dessus.
   */
  const [menuOuvert, setMenuOuvert] = useState(false)

  useEffect(() => {
    const charger = apercu
      ? storeId
        ? apercuVitrine(storeId)
        : null
      : slug
        ? lireVitrine(slug)
        : null
    if (!charger) return
    void charger
      .then(setSite)
      .catch((e) =>
        setErreur(
          e instanceof ApiError && e.status === 404
            ? apercu
              ? 'Aucun site à prévisualiser pour cette boutique.'
              : 'Cette page n’existe pas.'
            : 'Page momentanément indisponible.',
        ),
      )
  }, [slug, apercu, storeId])

  useEffect(() => {
    if (site) document.title = site.contenu.entete.titre || site.boutique.nom
  }, [site])

  if (erreur) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 6 }}>
        <Typography variant="h2">{erreur}</Typography>
      </Box>
    )
  }

  if (!site) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  const { contenu, boutique } = site
  const sombre = contenu.theme.ambiance === 'sombre'
  const couleur = contenu.theme.couleur
  const fond = sombre ? '#0f172a' : '#ffffff'
  const encre = sombre ? '#e2e8f0' : '#0f172a'
  const attenue = sombre ? '#94a3b8' : '#475569'
  const bandeau = sombre ? '#1e293b' : '#f8fafc'

  /**
   * ⚠️ L'ancienne image unique reprise quand aucune n'est déclarée.
   *
   * Les sites écrits avant le bandeau plein cadre ne portent qu'elle. L'ignorer
   * aurait fait disparaître leur visuel sans que personne n'ait rien touché.
   */
  const imagesHero =
    contenu.hero.images.length > 0
      ? contenu.hero.images
      : contenu.hero.image
        ? [contenu.hero.image]
        : []

  const ancre = (href: string): void => {
    document.getElementById(href.replace('#', ''))?.scrollIntoView({ behavior: 'smooth' })
  }

/** Choisir un service ouvre la modale sur CETTE prestation. */
  const prendreRdv = (itemId: string): void => {
    setServiceChoisi(itemId)
    setRdvOuvert(true)
  }

  const bouton = (
    b: { label: string; href: string; action: 'lien' | 'rendezvous' } | null | undefined,
    principal: boolean,
    /**
     * ⚠️ Sur une IMAGE, un bouton contourné dans la couleur du thème se noie :
     * le voile sombre l'assombrit avec le reste, et un liseré bleu marine sur
     * fond bleu marine ne se voit plus. Il passe en blanc, comme le texte.
     */
    surImage = false,
  ): JSX.Element | null => {
    if (!b?.label) return null
    const secondaire = surImage
      ? { borderColor: 'rgba(255,255,255,.75)', color: '#fff' }
      : { borderColor: couleur, color: couleur }
    return (
      <Button
        size="large"
        variant={principal ? 'contained' : 'outlined'}
        onClick={() =>
          b.action === 'rendezvous'
            ? setRdvOuvert(true)
            : b.href.startsWith('#')
              ? ancre(b.href)
              : window.open(b.href, '_blank', 'noopener')
        }
        sx={
          principal
            ? { bgcolor: couleur, '&:hover': { bgcolor: couleur, filter: 'brightness(0.92)' } }
            : secondaire
        }
      >
        {b.label}
      </Button>
    )
  }

  return (
    <Box sx={{ bgcolor: fond, color: encre, minHeight: '100vh' }}>
      {/* ⚠️ Le bandeau d'aperçu est VOYANT et non discret : on doit savoir au
          premier coup d'œil qu'on ne regarde pas ce que voient les clients. */}
      {apercu && (
        <Alert severity="warning" square sx={{ borderRadius: 0 }}>
          Prévisualisation — vous voyez vos modifications enregistrées, publiées ou non. Ce n’est
          pas nécessairement ce que voient les visiteurs.
        </Alert>
      )}

      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: fond,
          borderBottom: `1px solid ${sombre ? '#1e293b' : '#e2e8f0'}`,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, py: 3 }}
        >
          {(contenu.entete.logo ?? boutique.logoUrl) && (
            <Box
              component="img"
              src={contenu.entete.logo ?? boutique.logoUrl ?? ''}
              alt=""
              sx={{ height: 40, maxWidth: 160, objectFit: 'contain' }}
            />
          )}
          {/* Tronqué plutôt que débordant : un nom long poussait le bouton
              d'action hors de l'écran. */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {contenu.entete.titre || boutique.nom}
          </Typography>

          <Box sx={{ flex: 1 }} />

          {/* Le tiroir de navigation, sur téléphone seulement. */}
          {contenu.entete.nav.length > 0 && (
            <IconButton
              aria-label="Ouvrir le menu"
              onClick={() => setMenuOuvert(true)}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, color: encre }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 4 }}>
            {contenu.entete.nav.map((l) => (
              <Box
                key={l.href + l.label}
                component="a"
                onClick={() => ancre(l.href)}
                sx={{
                  cursor: 'pointer',
                  color: attenue,
                  fontSize: '.9rem',
                  '&:hover': { color: couleur },
                }}
              >
                {l.label}
              </Box>
            ))}
          </Box>

          {contenu.entete.cta?.label && (
            <Button
              variant="contained"
              onClick={() =>
                contenu.entete.cta!.action === 'rendezvous'
                  ? setRdvOuvert(true)
                  : ancre(contenu.entete.cta!.href)
              }
              sx={{
                bgcolor: couleur,
                '&:hover': { bgcolor: couleur },
                // ⚠️ Resserré sur téléphone : à pleine taille, le bouton
                // mangeait la moitié de la barre et écrasait le nom de la
                // boutique contre le logo.
                whiteSpace: 'nowrap',
                px: { xs: 2, sm: 4 },
                fontSize: { xs: '.8rem', sm: '.875rem' },
              }}
            >
              {contenu.entete.cta.label}
            </Button>
          )}
        </Container>
      </Box>

      <Hero
        titre={contenu.hero.titre}
        accroche={contenu.hero.accroche}
        images={imagesHero}
        bandeau={bandeau}
        attenue={attenue}
        boutons={
          <>
            {bouton(contenu.hero.boutonPrincipal, true)}
            {bouton(contenu.hero.boutonSecondaire, false, imagesHero.length > 0)}
          </>
        }
      />

      {contenu.sections
        .filter((s) => s.visible)
        // ⚠️ Le bloc de rendez-vous ne se dessine plus DANS la page : il est
        // devenu une modale, ouverte par les boutons. Le garder ici afficherait
        // le formulaire deux fois.
        .filter((s) => s.type !== 'rendezvous')
        .map((s, i) => (
          <Box
            key={s.id}
            id={s.id}
            component="section"
            sx={{ py: { xs: 8, md: 12 }, bgcolor: i % 2 === 1 ? bandeau : fond }}
          >
            <Container maxWidth="lg">
              {s.titre && (
                <Typography sx={{ fontSize: '1.8rem', fontWeight: 700, mb: s.sousTitre ? 1 : 5 }}>
                  {s.titre}
                </Typography>
              )}
              {s.sousTitre && <Typography sx={{ color: attenue, mb: 5 }}>{s.sousTitre}</Typography>}
              <Section
                section={s}
                site={site}
                couleur={couleur}
                attenue={attenue}
                serviceChoisi={serviceChoisi}
                onPrendreRdv={prendreRdv}
              />
            </Container>
          </Box>
        ))}

      {/* ⭐ Le menu en tiroir : les mêmes entrées que sur bureau, atteignables
          au pouce. Il se referme dès qu'on a choisi — on l'a ouvert pour aller
          quelque part, pas pour le contempler. */}
      <Drawer anchor="right" open={menuOuvert} onClose={() => setMenuOuvert(false)}>
        <Box sx={{ width: 260, bgcolor: fond, color: encre, height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            <IconButton aria-label="Fermer le menu" onClick={() => setMenuOuvert(false)}>
              <CloseIcon sx={{ color: encre }} />
            </IconButton>
          </Box>
          <List>
            {contenu.entete.nav.map((l) => (
              <ListItemButton
                key={l.href + l.label}
                onClick={() => {
                  setMenuOuvert(false)
                  // Le tiroir se ferme d'abord : défiler pendant qu'il glisse
                  // laissait la page à mi-chemin.
                  setTimeout(() => ancre(l.href), 220)
                }}
              >
                <ListItemText primary={l.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      {rdvOuvert && site.rdvEnLigne && (
        <ModaleRdvEnLigne
          slug={boutique.slug}
          prestations={site.prestations}
          prestationInitiale={serviceChoisi ?? undefined}
          horaires={site.horaires}
          couleur={couleur}
          onClose={() => setRdvOuvert(false)}
        />
      )}

      <Box sx={{ bgcolor: sombre ? '#020617' : '#0f172a', color: '#cbd5e1', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2">{contenu.pied.texte}</Typography>
            <Box sx={{ flex: 1 }} />
            {contenu.pied.liens.map((l) => (
              <Box
                key={l.href + l.label}
                component="a"
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#cbd5e1', fontSize: '.9rem' }}
              >
                {l.label}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

/** Le contenu propre à chaque type de bloc. */
function Section({
  section,
  site,
  couleur,
  attenue,
  serviceChoisi,
  onPrendreRdv,
}: {
  section: SectionSite
  site: SitePublic
  couleur: string
  attenue: string
  serviceChoisi: string | null
  onPrendreRdv: (itemId: string) => void
}): JSX.Element | null {
  const monnaie = (centimes: string): string =>
    new Intl.NumberFormat('fr-FR').format(Number(centimes))

  switch (section.type) {
    case 'texte':
      return (
        <Box
          sx={{
            display: 'grid',
            gap: 6,
            gridTemplateColumns: { xs: '1fr', md: section.image ? '1fr 1fr' : '1fr' },
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              whiteSpace: 'pre-line',
              color: attenue,
              lineHeight: 1.8,
              order: { md: section.imageADroite ? 0 : 1 },
            }}
          >
            {section.corps}
          </Typography>
          {section.image && (
            <Box
              component="img"
              src={section.image}
              alt=""
              sx={{ width: '100%', borderRadius: 4, objectFit: 'cover' }}
            />
          )}
        </Box>
      )

    case 'prestations':
    case 'produits': {
      // ⭐ Ce qui paraît est décidé ARTICLE PAR ARTICLE, par la case « visible
      // sur le site » : le serveur n'envoie que les articles cochés.
      const choisis = section.type === 'prestations' ? site.prestations : site.produits
      if (choisis.length === 0) return null
      return (
        <Carrousel>
          {choisis.map((a: ArticlePublic) => (
            <Box
              key={a.id}
              sx={{
                // ⚠️ Largeur FIXE et `flexShrink: 0` : dans un défilement
                // horizontal, des cartes élastiques se compriment toutes pour
                // tenir dans la largeur — il n'y aurait alors plus rien à faire
                // défiler.
                flex: '0 0 auto',
                width: { xs: 260, sm: 300 },
                scrollSnapAlign: 'start',
                border: '1px solid',
                borderColor: serviceChoisi === a.id ? couleur : 'rgba(148,163,184,.35)',
                borderRadius: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {a.image && (
                <Box
                  component="img"
                  src={a.image}
                  alt=""
                  sx={{ width: '100%', height: 170, objectFit: 'cover' }}
                />
              )}
              <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>{a.nom}</Typography>
                {a.description && (
                  <Typography variant="body2" sx={{ color: attenue, mt: 1 }}>
                    {a.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'center' }}>
                  {section.afficherPrix && (
                    <Typography sx={{ fontWeight: 700, color: couleur }}>
                      {monnaie(a.prix)}
                    </Typography>
                  )}
                  {section.type === 'prestations' &&
                    section.afficherDuree &&
                    a.dureeMinutes !== null && (
                      <Chip size="small" label={`${a.dureeMinutes} min`} variant="outlined" />
                    )}
                </Box>

                {/* ⭐ Chaque prestation porte SON bouton : c'est en lisant la
                    prestation qu'on décide de la réserver, pas en redescendant
                    la choisir dans une liste.
                    ⚠️ Seulement si la boutique a ouvert son carnet, et seulement
                    pour un service dont la durée est connue — sans elle, aucun
                    créneau ne peut être calculé. */}
                {section.type === 'prestations' && site.rdvEnLigne && a.dureeMinutes !== null && (
                  <Box sx={{ mt: 'auto', pt: 3 }}>
                    <Button
                      fullWidth
                      variant={serviceChoisi === a.id ? 'contained' : 'outlined'}
                      onClick={() => onPrendreRdv(a.id)}
                      sx={
                        serviceChoisi === a.id
                          ? { bgcolor: couleur, '&:hover': { bgcolor: couleur } }
                          : { borderColor: couleur, color: couleur }
                      }
                    >
                      Prendre rendez-vous
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Carrousel>
      )
    }

    case 'galerie':
      return <Galerie medias={section.medias} attenue={attenue} />

    case 'temoignages':
      return (
        <Box
          sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}
        >
          {section.avis.map((a, i) => (
            <Box
              key={i}
              sx={{
                p: 5,
                border: '1px solid',
                borderColor: 'rgba(148,163,184,.35)',
                borderRadius: 3,
              }}
            >
              <Typography sx={{ fontStyle: 'italic', lineHeight: 1.7 }}>« {a.texte} »</Typography>
              <Typography variant="body2" sx={{ mt: 3, color: attenue }}>
                — {a.auteur}
              </Typography>
            </Box>
          ))}
        </Box>
      )

    case 'horaires':
      return (
        <Box sx={{ maxWidth: 460 }}>
          {/* Lundi d'abord : le stockage commence le dimanche (convention
              `Date#getDay`), la lecture non. */}
          {[1, 2, 3, 4, 5, 6, 0].map((j) => {
            const journee = site.horaires.days[j]
            return (
              <Box key={j}>
                <Box sx={{ display: 'flex', py: 2, gap: 3 }}>
                  <Typography sx={{ width: 120, fontWeight: 500 }}>{JOURS_FR[j]}</Typography>
                  <Typography sx={{ color: attenue }}>
                    {!journee || journee.closed
                      ? 'Fermé'
                      : journee.ranges.map((p) => `${p.start} – ${p.end}`).join(' · ')}
                  </Typography>
                </Box>
                <Divider />
              </Box>
            )
          })}
          {section.note && (
            <Typography variant="body2" sx={{ mt: 3, color: attenue }}>
              {section.note}
            </Typography>
          )}
        </Box>
      )

    case 'contact':
      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          {section.afficherAdresse && site.boutique.adresse && (
            <Typography sx={{ color: attenue }}>{site.boutique.adresse}</Typography>
          )}
          {section.afficherTelephone && site.boutique.telephone && (
            <Typography
              component="a"
              href={`tel:${site.boutique.telephone}`}
              sx={{ color: couleur }}
            >
              {site.boutique.telephone}
            </Typography>
          )}
          {section.afficherEmail && site.boutique.email && (
            <Typography
              component="a"
              href={`mailto:${site.boutique.email}`}
              sx={{ color: couleur }}
            >
              {site.boutique.email}
            </Typography>
          )}
          {section.note && (
            <Typography sx={{ color: attenue, whiteSpace: 'pre-line', mt: 2 }}>
              {section.note}
            </Typography>
          )}
        </Box>
      )

    case 'rendezvous':
      // Devenu une modale : la page ne le dessine plus.
      return null

    default:
      return null
  }
}

/**
 * La galerie et son lecteur.
 *
 * ⭐ Un média s'ouvre en grand : une vignette de 180 pixels ne montre pas un
 * salon, et une vidéo réduite à cette taille ne se regarde pas. Les flèches
 * font défiler sans revenir à la grille — on parcourt, on ne clique pas
 * quinze fois.
 */
function Galerie({
  medias,
  attenue,
}: {
  medias: { type: 'image' | 'video'; url: string; legende: string }[]
  attenue: string
}): JSX.Element | null {
  const [ouvert, setOuvert] = useState<number | null>(null)
  if (medias.length === 0) return null

  const courant = ouvert === null ? null : medias[ouvert]
  const aller = (sens: -1 | 1): void =>
    setOuvert((i) => (i === null ? null : (i + sens + medias.length) % medias.length))

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        }}
      >
        {medias.map((m, i) => (
          <Box
            key={i}
            onClick={() => setOuvert(i)}
            sx={{
              position: 'relative',
              cursor: 'pointer',
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'rgba(148,163,184,.2)',
              height: 180,
            }}
          >
            {m.type === 'image' ? (
              <Box
                component="img"
                src={m.url}
                alt={m.legende}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              // ⚠️ La vidéo n'est PAS chargée en vignette : une grille de douze
              // lecteurs ferait ramer la page et consommerait le forfait du
              // visiteur avant qu'il n'ait rien demandé.
              <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                <PlayIcon sx={{ fontSize: 44, color: attenue }} />
              </Box>
            )}
            {m.legende && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(15,23,42,.65)',
                  color: '#fff',
                }}
              >
                {m.legende}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Dialog open={courant !== null} onClose={() => setOuvert(null)} maxWidth="lg" fullWidth>
        <Box sx={{ position: 'relative', bgcolor: '#000' }}>
          <IconButton
            aria-label="Fermer"
            onClick={() => setOuvert(null)}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>

          {medias.length > 1 && (
            <>
              <IconButton
                aria-label="Média précédent"
                onClick={() => aller(-1)}
                sx={{ position: 'absolute', top: '50%', left: 8, zIndex: 2, color: '#fff' }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
              <IconButton
                aria-label="Média suivant"
                onClick={() => aller(1)}
                sx={{ position: 'absolute', top: '50%', right: 8, zIndex: 2, color: '#fff' }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </>
          )}

          {courant?.type === 'image' ? (
            <Box
              component="img"
              src={courant.url}
              alt={courant.legende}
              sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block' }}
            />
          ) : courant ? (
            <Box
              component="video"
              src={courant.url}
              controls
              autoPlay
              sx={{ width: '100%', maxHeight: '80vh', display: 'block' }}
            />
          ) : null}

          {courant?.legende && (
            <Typography sx={{ color: '#fff', px: 4, py: 2 }}>{courant.legende}</Typography>
          )}
        </Box>
      </Dialog>
    </>
  )
}

/**
 * Le bandeau d'accueil : une image de FOND, plein cadre.
 *
 * ⭐ L'image occupe toute la surface et le texte se pose dessus — c'est ce qui
 * donne son atmosphère à une devanture. Posée à côté du texte, elle
 * ressemblait à l'illustration d'un article.
 *
 * ⭐ Plusieurs images défilent seules, en fondu : le commerçant montre son
 * salon, son équipe, une réalisation, sans que le visiteur ait à cliquer.
 *
 * ⚠️ Un VOILE sombre couvre l'image, toujours. Sans lui, un titre blanc sur une
 * photo claire devient illisible — et c'est précisément la photo qu'on ne
 * choisit pas qui pose le problème.
 */
function Hero({
  titre,
  accroche,
  images,
  bandeau,
  attenue,
  boutons,
}: {
  titre: string
  accroche: string
  images: string[]
  bandeau: string
  attenue: string
  boutons: JSX.Element
}): JSX.Element | null {
  const [courante, setCourante] = useState(0)

  useEffect(() => {
    if (images.length < 2) return
    // ⚠️ Défilement automatique SUSPENDU pour qui demande moins d'animations :
    // un fond qui bouge tout seul est exactement ce que ce réglage système
    // cherche à éviter, et il est souvent posé pour raison médicale.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    const minuterie = setInterval(() => setCourante((i) => (i + 1) % images.length), 6000)
    return () => clearInterval(minuterie)
  }, [images.length])

  if (!titre && images.length === 0) return null

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        // Sans image, le bandeau reste une bande colorée sobre : la hauteur
        // pleine page n'aurait alors rien à montrer.
        minHeight: images.length > 0 ? { xs: 460, md: 620 } : undefined,
        display: 'flex',
        alignItems: 'center',
        py: images.length > 0 ? 0 : { xs: 8, md: 14 },
        bgcolor: images.length > 0 ? '#0f172a' : bandeau,
      }}
    >
      {images.map((src, i) => (
        <Box
          key={i}
          component="img"
          src={src}
          alt=""
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Fondu enchaîné : deux images superposées dont l'une s'efface.
            opacity: i === courante ? 1 : 0,
            transition: 'opacity 1.2s ease-in-out',
          }}
        />
      ))}

      {images.length > 0 && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(15,23,42,.62) 0%, rgba(15,23,42,.45) 50%, rgba(15,23,42,.70) 100%)',
          }}
        />
      )}

      <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 10, md: 0 } }}>
        <Box sx={{ maxWidth: 720 }}>
          <Typography
            sx={{
              fontSize: { xs: '2.1rem', md: '3.4rem' },
              fontWeight: 800,
              lineHeight: 1.08,
              color: images.length > 0 ? '#fff' : 'inherit',
              textShadow: images.length > 0 ? '0 2px 18px rgba(0,0,0,.45)' : 'none',
            }}
          >
            {titre}
          </Typography>
          {accroche && (
            <Typography
              sx={{
                mt: 4,
                fontSize: '1.15rem',
                maxWidth: 620,
                color: images.length > 0 ? 'rgba(255,255,255,.92)' : attenue,
                textShadow: images.length > 0 ? '0 1px 12px rgba(0,0,0,.45)' : 'none',
              }}
            >
              {accroche}
            </Typography>
          )}
          <Box
            sx={{
              mt: 6,
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              // Empilés et pleine largeur sur téléphone : deux boutons de
              // largeurs différentes l'un sous l'autre font désordre.
              '& > button': { width: { xs: '100%', sm: 'auto' } },
            }}
          >
            {boutons}
          </Box>
        </Box>
      </Container>

      {/* Les pastilles disent combien d'images défilent, et permettent d'en
          choisir une — l'automatisme ne doit pas confisquer la main. */}
      {images.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          {images.map((_, i) => (
            <Box
              key={i}
              component="button"
              aria-label={`Image ${i + 1}`}
              onClick={() => setCourante(i)}
              sx={{
                width: i === courante ? 26 : 10,
                height: 10,
                p: 0,
                border: 0,
                borderRadius: 5,
                cursor: 'pointer',
                bgcolor: i === courante ? '#fff' : 'rgba(255,255,255,.5)',
                transition: 'width .3s, background-color .3s',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

/**
 * Un rail horizontal, avec ses flèches.
 *
 * ⭐ Les prestations et les produits défilent latéralement plutôt que de
 * s'empiler en grille : une enseigne en propose dix ou vingt, et une grille
 * repoussait tout le reste de la page — horaires, contact — sous trois écrans
 * de cartes.
 *
 * ⚠️ Le défilement natif est CONSERVÉ (doigt, pavé tactile, molette
 * horizontale) et les flèches ne font que s'y ajouter. Les remplacer par un
 * carrousel piloté aurait cassé le geste que tout le monde connaît sur
 * téléphone — c'est-à-dire là où ces pages se lisent.
 */
function Carrousel({ children }: { children: React.ReactNode }): JSX.Element {
  const rail = useRef<HTMLDivElement>(null)

  const glisser = (sens: -1 | 1): void => {
    const el = rail.current
    if (!el) return
    el.scrollBy({ left: sens * Math.max(280, el.clientWidth * 0.8), behavior: 'smooth' })
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={rail}
        sx={{
          display: 'flex',
          gap: 4,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          pb: 2,
          // La barre de défilement est masquée : les flèches et le geste
          // suffisent, et une barre grise sous chaque rangée alourdit la page.
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {children}
      </Box>

      {/* Masquées sur mobile : on y fait glisser au doigt, et deux ronds
          par-dessus les cartes voleraient la place. */}
      {(['gauche', 'droite'] as const).map((cote) => (
        <IconButton
          key={cote}
          aria-label={cote === 'gauche' ? 'Faire défiler vers la gauche' : 'Faire défiler vers la droite'}
          onClick={() => glisser(cote === 'gauche' ? -1 : 1)}
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'absolute',
            top: '45%',
            [cote === 'gauche' ? 'left' : 'right']: -18,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          {cote === 'gauche' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      ))}
    </Box>
  )
}
